import React, {useEffect, useRef, useState} from 'react';
import {Alert, Button, Card, Spin} from 'antd';
import {FormattedMessage, useIntl} from 'react-intl';
import {Link, useSearchParams} from 'react-router-dom';
import {isMobileOnly} from 'react-device-detect';
import {getAuthenticationApi} from '../api/apiInstances';
import {useTrackEvent} from '../hooks/useTrackEvent';

const VerifyEmailPage = () => {
    const intl = useIntl();
    const track = useTrackEvent();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') ?? '';
    const emailParam = searchParams.get('email') ?? '';

    // ── Token-confirm mode ────────────────────────────────────────────────────
    type ConfirmState = 'loading' | 'success' | 'invalid';
    const [confirmState, setConfirmState] = useState<ConfirmState>('loading');
    const [confirmErrMsg, setConfirmErrMsg] = useState('');

    // Guard: prevent double-fire when intl reference changes between renders
    const confirmCalledRef = useRef(false);
    // Guard: fire email_verification_sent once per check-inbox mount
    const sentRef = useRef(false);

    // ── Check-inbox mode (email param present, no token) ──────────────────────
    const [resendLoading, setResendLoading] = useState(false);
    const [resendCountdown, setResendCountdown] = useState(0); // seconds until resend allowed
    const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const startCountdown = (seconds: number) => {
        setResendCountdown(seconds);
        if (countdownRef.current) clearInterval(countdownRef.current);
        countdownRef.current = setInterval(() => {
            setResendCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(countdownRef.current!);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    useEffect(() => {
        return () => {
            if (countdownRef.current) clearInterval(countdownRef.current);
        };
    }, []);

    // Email-mode: user has just registered and been told to check their inbox.
    // Fires once per mount — marks the step in the funnel where the verification
    // email is in the user's inbox (or spam) and we're waiting for them to click it.
    useEffect(() => {
        if (!emailParam || token) return;     // token-mode — skip
        if (sentRef.current) return;
        sentRef.current = true;
        track('email_verification_sent');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Confirm flow: only runs when token is present, exactly once
    useEffect(() => {
        if (!token) return; // email-mode — skip confirm
        if (confirmCalledRef.current) return; // guard against re-fire (e.g. intl ref change)
        confirmCalledRef.current = true;

        if (!token.trim()) {
            setConfirmErrMsg(intl.formatMessage({id: 'verify_email_invalid_token'}));
            setConfirmState('invalid');
            return;
        }

        (async () => {
            try {
                await getAuthenticationApi().confirmUserEmailVerification({token});
                setConfirmState('success');
                track('email_verification_completed');
                // Auto-redirect to login after 3 seconds
                setTimeout(() => {
                    window.location.href = '/login';
                }, 3000);
            } catch (err: any) {
                const detail = err?.response?.data?.detail;
                const alreadyUsed = typeof detail === 'string' && detail.toLowerCase().includes('already');
                if (alreadyUsed) {
                    setConfirmErrMsg(intl.formatMessage({id: 'verify_email_already_used'}));
                } else {
                    setConfirmErrMsg(intl.formatMessage({id: 'verify_email_invalid_token'}));
                }
                setConfirmState('invalid');
                track('email_verification_failed', { reason: alreadyUsed ? 'already_used' : 'token_invalid' });
            }
        })();
    }, [token, intl]);

    const handleResend = async () => {
        if (resendLoading || resendCountdown > 0) return;
        setResendLoading(true);
        try {
            await getAuthenticationApi().publicResendEmailVerification({email: emailParam});
            startCountdown(60);
        } catch (err: any) {
            const retryAfter = err?.response?.data?.detail?.retry_after_seconds;
            if (retryAfter) {
                startCountdown(retryAfter);
            } else {
                startCountdown(60); // fallback
            }
        } finally {
            setResendLoading(false);
        }
    };

    const cardStyle: React.CSSProperties = {
        width: isMobileOnly ? '90vw' : 480,
        padding: '40px',
        borderRadius: '30px',
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    };

    // ── Email-mode (check inbox) ───────────────────────────────────────────────
    if (emailParam && !token) {
        return (
            <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh'}}>
                <div style={{marginBottom: '20px', textAlign: 'center'}}>
                    <a href="https://dentobook.com/en/" target="_blank" rel="noopener noreferrer">
                        <img
                            alt="logo"
                            className="dentobook-logo"
                            src={require('../assets/logo.png')}
                            style={{width: '420px', height: '104px', padding: '13px 10px 13.65px 10px'}}
                        />
                    </a>
                </div>

                <Card style={cardStyle}>
                    <div style={{textAlign: 'center'}}>
                        <h2 style={{marginBottom: '12px', fontSize: '24px', fontWeight: 600}}>
                            <FormattedMessage id="verify_email_page_title"/>
                        </h2>
                        <p style={{color: '#666', marginBottom: '8px', lineHeight: 1.6}}>
                            <FormattedMessage id="verify_email_check_inbox_subtitle" values={{email: <strong>{emailParam}</strong>}}/>
                        </p>
                        <p style={{color: '#999', marginBottom: '24px', fontSize: '13px'}}>
                            <FormattedMessage id="verify_email_spam_hint"/>
                        </p>

                        <Button
                            type="primary"
                            onClick={handleResend}
                            disabled={resendLoading || resendCountdown > 0}
                            style={{width: '100%', height: '44px', borderRadius: '12px', marginBottom: '12px'}}
                        >
                            {resendLoading
                                ? '...'
                                : resendCountdown > 0
                                    ? intl.formatMessage({id: 'verify_email_resend_countdown'}, {seconds: resendCountdown})
                                    : intl.formatMessage({id: 'email_not_verified_resend'})
                            }
                        </Button>

                        <Link to="/login">
                            <Button style={{width: '100%', height: '44px', borderRadius: '12px'}}>
                                <FormattedMessage id="verify_email_wrong_email"/>
                            </Button>
                        </Link>
                    </div>
                </Card>
            </div>
        );
    }

    // ── Token-confirm mode ────────────────────────────────────────────────────
    return (
        <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh'}}>
            <div style={{marginBottom: '20px', textAlign: 'center'}}>
                <a href="https://dentobook.com/en/" target="_blank" rel="noopener noreferrer">
                    <img
                        alt="logo"
                        className="dentobook-logo"
                        src={require('../assets/logo.png')}
                        style={{width: '420px', height: '104px', padding: '13px 10px 13.65px 10px'}}
                    />
                </a>
            </div>

            <Card style={cardStyle}>
                <div style={{marginBottom: '24px', textAlign: 'center'}}>
                    <h2 style={{marginBottom: '8px', fontSize: '24px', fontWeight: 600}}>
                        <FormattedMessage id="verify_email_page_title"/>
                    </h2>
                </div>

                {confirmState === 'loading' && (
                    <div style={{textAlign: 'center', padding: '40px 0'}}>
                        <Spin size="large"/>
                    </div>
                )}

                {confirmState === 'success' && (
                    <>
                        <Alert
                            message={<FormattedMessage id="verify_email_success_title"/>}
                            description={<FormattedMessage id="verify_email_success_subtitle"/>}
                            type="success"
                            showIcon
                        />
                        <div style={{textAlign: 'center', marginTop: '24px'}}>
                            <Link to="/login" style={{color: '#1890ff', fontWeight: 500}}>
                                <FormattedMessage id="verify_email_success_login"/>
                            </Link>
                        </div>
                    </>
                )}

                {confirmState === 'invalid' && (
                    <>
                        <Alert message={confirmErrMsg} type="error" showIcon/>
                        <div style={{textAlign: 'center', marginTop: '16px'}}>
                            <Link to="/login" style={{color: '#1890ff', fontWeight: 500}}>
                                <FormattedMessage id="back_to_login"/>
                            </Link>
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
};

export default VerifyEmailPage;
